import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  MapPin,
  RefreshCw,
  Search,
  CheckCircle,
  Wifi,
  WifiOff,
  Route as RouteIcon,
  ChevronDown,
  ChevronUp,
  MoreHorizontal,
  History as HistoryIcon,
  Sunrise,
  Sunset,
} from 'lucide-react';
import InteractiveMap from '../components/InteractiveMap';
import ParentConfirmedTick from '../components/ParentConfirmedTick';
import DriverCard from '../components/DriverCard';
import { Button } from '@/components/ui/button';
import { useGpsSuivi } from '../hooks/trGpsSuivi';
import PageError from '../components/PageError';
import logger from '@/utils/logger';

const getBadgeForStatus = (status) => {
  const BADGES = {
    'En route': (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-green-500 animate-pulse" />
        En route
      </span>
    ),
    Arrêté: (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
        <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-orange-500" />
        Arrêté
      </span>
    ),
    Garé: (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
        <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-amber-500 animate-pulse" />
        Garé · Moteur coupé
      </span>
    ),
    'Signal perdu': (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
        <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-red-500 animate-pulse" />
        Signal perdu
      </span>
    ),
    Prêt: (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
        <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-blue-500" />
        Prêt
      </span>
    ),
    Terminé: (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <span className="w-1.5 h-1.5 rounded-full mr-1.5 bg-gray-500" />
        Terminé
      </span>
    ),
  };
  return (
    BADGES[status] ?? (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        Inactif
      </span>
    )
  );
};

const UnifiedTracking = () => {
  const {
    stops,
    loading,
    error,
    schoolInfo,
    center,
    wsConnected,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    showRoutes,
    setShowRoutes,
    mapRouteStopsDirection,
    setMapRouteStopsDirection,
    loadingCircuits,
    circuits,
    showStops,
    setShowStops,
    expandedRows,
    tripDetails,
    loadingTripDetails,
    activeMenu,
    setActiveMenu,
    selectedDriver,
    setSelectedDriver,
    filteredCircuits,
    allVehiclesForMap,
    refreshGPSData,
    loadCircuits,
    toggleRow,
    navigate,
    selectedVehicle,
    setSelectedVehicle,
    isImpersonating,
    handleForceEndTrip,
  } = useGpsSuivi();

  return (
    <div className="h-full flex flex-col space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-2 gap-3">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold text-gray-900">Suivi GPS & Temps Réel</h1>
          <p className="text-sm text-gray-500">Vue d&apos;ensemble de la flotte et des circuits</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate('/admin/history')}>
            <HistoryIcon className="h-4 w-4" />
            Historique
          </Button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Carte */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden h-[280px] sm:h-[380px] lg:h-[500px] relative">
          {error ? (
            <div className="h-full flex items-center justify-center">
              <PageError
                error="Failed to fetch"
                subtitle="Le suivi en temps réel est indisponible. Réessayez pour reconnecter."
                onRetry={refreshGPSData}
                variant="inline"
              />
            </div>
          ) : null}
          {!error && (
            <InteractiveMap
              vehicles={allVehiclesForMap}
              schools={schoolInfo ? [schoolInfo] : []}
              routes={showRoutes ? circuits : []}
              circuits={showRoutes ? circuits : []}
              routeStopsDirection={mapRouteStopsDirection}
              showStopMarkers={showStops}
              stops={showStops ? stops : []}
              center={center}
              zoom={13}
              height="100%"
              selectedVehicle={selectedVehicle}
              onVehicleSelect={setSelectedVehicle}
              showTraffic={false}
              showWeather={false}
            />
          )}

          {!error && (
            <div className="absolute top-4 left-4 flex flex-col gap-2 z-10">
              <button
                onClick={() => {
                  if (!showRoutes && circuits.length === 0) loadCircuits();
                  setShowRoutes(!showRoutes);
                }}
                disabled={loadingCircuits}
                className={`p-2 rounded-lg shadow-sm text-sm font-medium transition-colors flex items-center justify-center ${showRoutes ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                title="Afficher/Masquer les circuits"
              >
                <RouteIcon size={18} className={loadingCircuits ? 'animate-spin' : ''} />
              </button>
              {showRoutes && (
                <div className="flex flex-col gap-1 rounded-lg shadow-sm overflow-hidden border border-gray-200 bg-white">
                  <button
                    type="button"
                    onClick={() => {
                      logger.info('🔀 [Direction] Bouton cliqué: inbound');
                      setMapRouteStopsDirection('inbound');
                    }}
                    className={`p-2 flex items-center justify-center text-sm font-medium transition-colors ${mapRouteStopsDirection === 'inbound' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                    title="Ordre des arrêts : aller"
                  >
                    <Sunrise size={16} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      logger.info('🔀 [Direction] Bouton cliqué: outbound');
                      setMapRouteStopsDirection('outbound');
                    }}
                    className={`p-2 flex items-center justify-center text-sm font-medium transition-colors border-t border-gray-100 ${mapRouteStopsDirection === 'outbound' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                    title="Ordre des arrêts : retour"
                  >
                    <Sunset size={16} />
                  </button>
                </div>
              )}
              <button
                onClick={() => setShowStops(!showStops)}
                className={`p-2 rounded-lg shadow-sm text-sm font-medium transition-colors ${showStops ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                title="Afficher/Masquer les arrêts"
              >
                <MapPin size={18} />
              </button>
            </div>
          )}
        </div>

        {/* Filtres */}
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Rechercher un circuit, chauffeur ou bus..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap items-center gap-2 w-full md:w-auto">
            <select
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">Tous les statuts</option>
              <option value="active">En cours</option>
              <option value="inactive">Inactif</option>
            </select>
            <button
              onClick={refreshGPSData}
              className={`p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors ${loading ? 'animate-spin' : ''}`}
              title="Actualiser"
            >
              <RefreshCw size={18} className="text-gray-600" />
            </button>
            <div
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium ${wsConnected ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
            >
              {wsConnected ? <Wifi size={14} /> : <WifiOff size={14} />}
              {wsConnected ? 'Connecté' : 'Déconnecté'}
            </div>
          </div>
        </div>

        {/* Tableau / Cards — circuits */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {error && (
            <PageError
              error="Failed to fetch"
              subtitle="Le suivi en temps réel est indisponible. Réessayez pour reconnecter."
              onRetry={refreshGPSData}
              variant="inline"
            />
          )}
          {!error && (
            <>
              {/* ── Vue cartes (mobile < md) ── */}
              <div className="md:hidden divide-y divide-gray-100">
                {filteredCircuits.length === 0 ? (
                  <div className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center justify-center">
                      <div className="p-3 bg-gray-100 rounded-full mb-3">
                        <Search size={24} className="text-gray-400" />
                      </div>
                      <p className="text-lg font-medium text-gray-900">Aucun circuit trouvé</p>
                      <p className="text-sm text-gray-500 mt-1">Essayez de modifier vos filtres de recherche</p>
                    </div>
                  </div>
                ) : (
                  filteredCircuits.map((circuit) => {
                    const rowId =
                      circuit.tripId != null && circuit.tripId !== '' ? String(circuit.tripId) : `route-${circuit.id}`;
                    const isExpanded = expandedRows[rowId];
                    const details = tripDetails[rowId];
                    const isLoadingDetails = loadingTripDetails[rowId];
                    return (
                      <div key={circuit.id} className={`${isExpanded ? 'bg-blue-50/30' : ''}`}>
                        {/* Card header */}
                        <div
                          className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
                          onClick={() => toggleRow(circuit.tripId, circuit.id)}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="p-2 bg-blue-100 text-blue-600 rounded-lg shrink-0">
                                <RouteIcon size={16} />
                              </div>
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900 truncate">{circuit.name}</div>
                                <div className="text-xs text-gray-500">{circuit.code || 'Sans code'}</div>
                              </div>
                            </div>
                            {getBadgeForStatus(circuit.status)}
                          </div>
                          <div className="mt-2.5 ml-[44px] space-y-1.5">
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <span className="font-medium">{circuit.busNumber}</span>
                              <span className="text-gray-400">·</span>
                              <span className="text-gray-500 text-xs truncate">{circuit.driverName}</span>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="flex items-center gap-1 text-gray-500 text-xs">
                                <MapPin size={12} className="shrink-0" />
                                <span>
                                  {circuit.studentsOnboard} / {circuit.studentsTotal}
                                </span>
                              </div>
                              {circuit.activeBus && circuit.status !== 'Inactif' && circuit.status !== 'Terminé' && (
                                <span
                                  className={`text-xs font-medium ${circuit.delay > 5 ? 'text-red-600' : circuit.delay < -2 ? 'text-green-600' : 'text-gray-400'}`}
                                >
                                  {circuit.delay > 0
                                    ? `+${circuit.delay} min`
                                    : circuit.delay < 0
                                      ? `${circuit.delay} min`
                                      : "À l'heure"}
                                </span>
                              )}
                              {circuit.activeBus?.buffered === true && (
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                                  Différé
                                </span>
                              )}
                            </div>
                            {circuit.activeBus && circuit.status !== 'Inactif' && (
                              <div className="w-full bg-gray-200 rounded-full h-1.5">
                                <div
                                  className={`h-1.5 rounded-full transition-all duration-500 ${circuit.delay > 5 ? 'bg-red-500' : circuit.delay < -2 ? 'bg-green-500' : 'bg-blue-600'}`}
                                  style={{
                                    width: `${circuit.progress || 0}%`,
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Expanded stop list */}
                        <AnimatePresence>
                          {isExpanded && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="border-t border-gray-100 bg-gray-50/50"
                            >
                              <div className="p-4">
                                <div className="flex items-center justify-between mb-3">
                                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                    <MapPin size={14} className="text-blue-500" />
                                    Liste des arrêts
                                  </h4>
                                  {details && (
                                    <span
                                      className={`px-2 py-0.5 rounded-full text-xs font-medium border ${details.isRealTime || details.status === 'active' || details.status === 'in_progress' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}
                                    >
                                      {details.isRealTime ||
                                      details.status === 'active' ||
                                      details.status === 'in_progress'
                                        ? 'Temps réel'
                                        : 'Planifié'}
                                    </span>
                                  )}
                                </div>
                                {isLoadingDetails ? (
                                  <div className="flex justify-center py-4">
                                    <RefreshCw className="animate-spin text-blue-500" />
                                  </div>
                                ) : details ? (
                                  <div className="relative border-l-2 border-gray-200 ml-2 space-y-4">
                                    {(details.stops || []).map((stop, idx) => (
                                      <div key={stop.stopId || stop.id || idx} className="relative pl-5">
                                        <div
                                          className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 ${stop.isCompleted ? 'bg-green-500 border-green-500' : stop.status === 'skipped' ? 'bg-red-500 border-red-500' : 'bg-white border-gray-300'}`}
                                        >
                                          {stop.isCompleted && (
                                            <CheckCircle size={12} className="text-white absolute top-0 left-0" />
                                          )}
                                        </div>
                                        <div className="flex justify-between items-start">
                                          <div>
                                            <div
                                              className={`text-sm font-medium flex items-center gap-1.5 ${stop.isCompleted ? 'text-gray-900' : 'text-gray-500'}`}
                                            >
                                              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold shrink-0">
                                                {idx + 1}
                                              </span>
                                              {stop.stopName}
                                            </div>
                                            <div className="text-xs text-gray-400 ml-6">
                                              {stop.studentsCount} élève(s)
                                            </div>
                                          </div>
                                          <div
                                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${stop.isCompleted ? 'bg-green-100 text-green-700' : stop.status === 'skipped' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}
                                          >
                                            {stop.isCompleted
                                              ? 'Visité'
                                              : stop.status === 'skipped'
                                                ? 'Sauté'
                                                : 'En attente'}
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                ) : (
                                  <p className="text-sm text-gray-500 italic">
                                    La liste d&apos;arrêts s&apos;affiche uniquement lorsqu&apos;un trajet est en cours.
                                  </p>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    );
                  })
                )}
              </div>

              {/* ── Tableau (desktop ≥ md) ── */}
              <div className="overflow-x-auto hidden md:block">
                <table className="min-w-[900px] w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider w-10"></th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Circuit
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Bus & Chauffeur
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Progression
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Arrêts
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredCircuits.length > 0 ? (
                      filteredCircuits.map((circuit) => {
                        const rowId =
                          circuit.tripId != null && circuit.tripId !== ''
                            ? String(circuit.tripId)
                            : `route-${circuit.id}`;
                        const isExpanded = expandedRows[rowId];
                        const details = tripDetails[rowId];
                        const isLoadingDetails = loadingTripDetails[rowId];

                        return (
                          <React.Fragment key={circuit.id}>
                            <tr
                              className={`hover:bg-gray-50 transition-colors cursor-pointer ${isExpanded ? 'bg-blue-50/30' : ''}`}
                              onClick={() => toggleRow(circuit.tripId, circuit.id)}
                            >
                              <td className="px-6 py-4">
                                {isExpanded ? (
                                  <ChevronUp size={18} className="text-gray-400" />
                                ) : (
                                  <ChevronDown size={18} className="text-gray-400" />
                                )}
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-3">
                                  <div className="p-2 bg-blue-100 text-blue-600 rounded-lg">
                                    <RouteIcon size={18} />
                                  </div>
                                  <div>
                                    <div className="font-medium text-gray-900">{circuit.name}</div>
                                    <div className="text-xs text-gray-500">{circuit.code || 'Sans code'}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex flex-col">
                                  <span className="text-sm font-medium text-gray-900">{circuit.busNumber}</span>
                                  <span className="text-xs text-gray-500">{circuit.driverName}</span>
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  {getBadgeForStatus(circuit.status)}
                                  {circuit.activeBus?.buffered === true && (
                                    <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 border border-amber-200">
                                      Différé
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1">
                                  <div
                                    className={`h-2.5 rounded-full transition-all duration-500 ${circuit.status === 'Inactif' || circuit.status === 'Terminé' ? 'bg-gray-400' : circuit.delay > 5 ? 'bg-red-500' : circuit.delay < -2 ? 'bg-green-500' : 'bg-blue-600'}`}
                                    style={{
                                      width:
                                        circuit.activeBus ||
                                        circuit.status === 'Terminé' ||
                                        circuit.status === 'Inactif'
                                          ? `${circuit.progress || 0}%`
                                          : '0%',
                                    }}
                                  />
                                </div>
                                <div className="flex justify-between items-center text-xs">
                                  <span className="text-gray-600 font-medium">
                                    {circuit.status === 'Inactif' || circuit.status === 'Terminé'
                                      ? 'Trajet terminé'
                                      : circuit.activeBus
                                        ? circuit.nextStop && circuit.nextStop !== 'N/A'
                                          ? `Vers: ${circuit.nextStop}`
                                          : 'En route'
                                        : 'Non démarré'}
                                  </span>
                                  {circuit.activeBus &&
                                    circuit.status !== 'Inactif' &&
                                    circuit.status !== 'Terminé' && (
                                      <span
                                        className={
                                          circuit.delay > 5
                                            ? 'text-red-600 font-bold'
                                            : circuit.delay < -2
                                              ? 'text-green-600 font-bold'
                                              : 'text-gray-500'
                                        }
                                      >
                                        {circuit.delay > 0
                                          ? `+${circuit.delay} min`
                                          : circuit.delay < 0
                                            ? `${circuit.delay} min`
                                            : "À l'heure"}
                                      </span>
                                    )}
                                </div>
                              </td>
                              <td className="px-6 py-4">
                                <div className="flex items-center gap-1 text-gray-600">
                                  <MapPin size={16} className="shrink-0" aria-hidden />
                                  <span className="text-sm">
                                    {circuit.studentsOnboard} / {circuit.studentsTotal}
                                  </span>
                                </div>
                              </td>
                              <td className="px-6 py-4 text-right relative">
                                <button
                                  className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setActiveMenu(activeMenu === rowId ? null : rowId);
                                  }}
                                >
                                  <MoreHorizontal size={18} />
                                </button>
                                {activeMenu === rowId && (
                                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 border border-gray-200 py-1 text-left">
                                    <button
                                      className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleRow(circuit.tripId, circuit.id);
                                        setActiveMenu(null);
                                      }}
                                    >
                                      Voir détails complets
                                    </button>
                                    <button
                                      className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        navigate(`/admin/history?route_id=${circuit.id}`);
                                        setActiveMenu(null);
                                      }}
                                    >
                                      Historique des trajets
                                    </button>
                                    <button
                                      className="block w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedDriver({
                                          name: circuit.driverName,
                                          phone: circuit.driverPhone || circuit.activeBus?.driver_phone,
                                          busNumber: circuit.busNumber,
                                          photo: circuit.driverPhoto,
                                          status: circuit.activeBus ? 'active' : 'inactive',
                                          lastLocation: circuit.activeBus
                                            ? `${circuit.activeBus.latitude.toFixed(4)}, ${circuit.activeBus.longitude.toFixed(4)}`
                                            : null,
                                        });
                                        setActiveMenu(null);
                                      }}
                                    >
                                      Contacter chauffeur
                                    </button>
                                    {isImpersonating &&
                                      circuit.tripId &&
                                      !String(circuit.tripId).startsWith('trip-') && (
                                        <button
                                          className="block w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleForceEndTrip(circuit.tripId, circuit.busNumber);
                                            setActiveMenu(null);
                                          }}
                                        >
                                          Terminer le trajet
                                        </button>
                                      )}
                                  </div>
                                )}
                              </td>
                            </tr>

                            <AnimatePresence>
                              {isExpanded && (
                                <motion.tr
                                  initial={{ opacity: 0, height: 0 }}
                                  animate={{ opacity: 1, height: 'auto' }}
                                  exit={{ opacity: 0, height: 0 }}
                                >
                                  <td colSpan="7" className="px-0 py-0 border-b border-gray-100 bg-gray-50/50">
                                    <div className="p-6 pl-20">
                                      <div className="flex items-center justify-between mb-4">
                                        <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                                          <MapPin size={16} className="text-blue-500" />
                                          Liste des arrêts
                                        </h4>
                                        {details && (
                                          <span
                                            className={`px-2.5 py-1 rounded-full text-xs font-medium border ${details.isRealTime || details.status === 'active' || details.status === 'in_progress' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-gray-50 text-gray-600 border-gray-200'}`}
                                          >
                                            {details.isRealTime ||
                                            details.status === 'active' ||
                                            details.status === 'in_progress'
                                              ? 'Données en temps réel'
                                              : 'Données planifiées (Trajet non démarré)'}
                                          </span>
                                        )}
                                      </div>
                                      {isLoadingDetails ? (
                                        <div className="flex justify-center py-4">
                                          <RefreshCw className="animate-spin text-blue-500" />
                                        </div>
                                      ) : details ? (
                                        <div className="relative border-l-2 border-gray-200 ml-3 space-y-6">
                                          {(details.stops || []).map((stop, idx) => (
                                            <div key={stop.stopId || stop.id || idx} className="relative pl-6">
                                              <div
                                                className={`absolute -left-[9px] top-1 w-4 h-4 rounded-full border-2 ${stop.isCompleted ? 'bg-green-500 border-green-500' : stop.status === 'skipped' ? 'bg-red-500 border-red-500' : 'bg-white border-gray-300'}`}
                                              >
                                                {stop.isCompleted && (
                                                  <CheckCircle size={12} className="text-white absolute top-0 left-0" />
                                                )}
                                              </div>
                                              <div className="flex justify-between items-start">
                                                <div>
                                                  <div
                                                    className={`text-sm font-medium flex items-center gap-1.5 ${stop.isCompleted ? 'text-gray-900' : 'text-gray-500'}`}
                                                  >
                                                    <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-50 text-blue-600 text-xs font-semibold shrink-0">
                                                      {idx + 1}
                                                    </span>
                                                    {stop.stopName}
                                                  </div>
                                                  <div className="text-xs text-gray-400 mt-0.5 ml-6">
                                                    {stop.studentsCount} élève(s) à récupérer
                                                  </div>
                                                </div>
                                                <div className="text-right">
                                                  <div
                                                    className={`text-xs font-medium px-2 py-1 rounded-full ${stop.isCompleted ? 'bg-green-100 text-green-700' : stop.status === 'skipped' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-600'}`}
                                                  >
                                                    {stop.isCompleted
                                                      ? 'Visité'
                                                      : stop.status === 'skipped'
                                                        ? 'Sauté'
                                                        : 'En attente'}
                                                  </div>
                                                  {stop.actualArrival && (
                                                    <div className="text-xs text-gray-400 mt-1">
                                                      {new Date(stop.actualArrival).toLocaleTimeString([], {
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                      })}
                                                    </div>
                                                  )}
                                                </div>
                                              </div>
                                              {stop.students && stop.students.length > 0 && (
                                                <div className="mt-2 pl-2 border-l border-gray-100 space-y-1">
                                                  {stop.students.map((student, si) => (
                                                    <div
                                                      key={student.id || si}
                                                      className="flex items-center gap-1 text-xs text-gray-600"
                                                    >
                                                      {student.firstName || student.first_name}{' '}
                                                      {student.lastName || student.last_name}
                                                      {/* Reflet passif du tap parent sur l'écran de suivi live —
                                                          signal purement observationnel, affiché sur les deux tiers.
                                                          Tap/clic pour afficher (pas seulement hover), voir
                                                          ParentConfirmedTick. */}
                                                      <ParentConfirmedTick confirmedAt={student.parentConfirmedAt} />
                                                    </div>
                                                  ))}
                                                </div>
                                              )}
                                            </div>
                                          ))}
                                        </div>
                                      ) : (
                                        <div className="text-sm text-gray-500 italic">
                                          La liste d&apos;arrêts s&apos;affiche uniquement lorsqu&apos;un trajet est en
                                          cours (données temps réel).
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </motion.tr>
                              )}
                            </AnimatePresence>
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                          <div className="flex flex-col items-center justify-center">
                            <div className="p-3 bg-gray-100 rounded-full mb-3">
                              <Search size={24} className="text-gray-400" />
                            </div>
                            <p className="text-lg font-medium text-gray-900">Aucun circuit trouvé</p>
                            <p className="text-sm text-gray-500 mt-1">Essayez de modifier vos filtres de recherche</p>
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {selectedDriver && (
        <DriverCard driver={selectedDriver} isOpen={!!selectedDriver} onClose={() => setSelectedDriver(null)} />
      )}
    </div>
  );
};

export default UnifiedTracking;
